// Builds the MUI theme from the company's server-driven colours, delivered on
// the logged-in user record (Users ⋈ CompanyInfo):
//   BackColor   → app background + surfaces   (Flutter colorFromAPI)
//   ButtonColor → header, buttons, device tiles (Flutter buttonFromAPI)
//   FontColor   → all foreground text          (Flutter textFromAPI)
// Colours arrive as Flutter ARGB (e.g. "0xFF8C8C8C"); normalizeHex handles that.

import { createTheme } from '@mui/material/styles';

export function normalizeHex(hex, fallback) {
  if (hex == null || hex === '') return fallback;
  // The server sends colours in Flutter ARGB form, e.g. "0xFF8C8C8C", and
  // sometimes as "#AARRGGBB", "RRGGBB", or "#RGB". Normalise all of them.
  let h = String(hex).trim()
    .replace(/^#/, '')       // drop a leading '#'
    .replace(/^0x/i, '');    // drop a Flutter '0x' prefix
  // 8 hex digits = AARRGGBB → drop the leading alpha byte.
  if (/^[0-9a-fA-F]{8}$/.test(h)) h = h.slice(2);
  return /^([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(h) ? `#${h}` : fallback;
}

export function colorsFromUser(user) {
  const u = user || {};
  return {
    back: normalizeHex(u.BackColor, '#101820'),
    button: normalizeHex(u.ButtonColor, '#37474F'),
    text: normalizeHex(u.FontColor, '#FFFFFF'),
  };
}

// Translucent variants of the font colour for borders / muted text, so the whole
// palette still derives from the record's three colours whatever they are.
function withAlpha(hex, a) {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function buildTheme(user) {
  const c = colorsFromUser(user);
  // eslint-disable-next-line no-console
  console.log('[Theme] applied colours', c, '(raw:', {
    BackColor: user && user.BackColor, ButtonColor: user && user.ButtonColor, FontColor: user && user.FontColor,
  }, ')');
  return createTheme({
    palette: {
      mode: 'dark',
      background: { default: c.back, paper: c.back },
      primary: { main: c.button, contrastText: c.text },
      secondary: { main: c.text, contrastText: c.button },
      text: { primary: c.text, secondary: withAlpha(c.text, 0.72) },
      divider: withAlpha(c.text, 0.24),
    },
    shape: { borderRadius: 10 },
    // Expose the raw colours so components can read them like the Flutter globals.
    sli: c,
    components: {
      // Header + all page app bars use the company Button colour with Font text.
      MuiAppBar: {
        defaultProps: { color: 'primary' },
        styleOverrides: { root: { backgroundColor: c.button, color: c.text } },
      },
      // Dialog / menu surfaces follow the background colour, not MUI's default.
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: c.button, color: c.text, border: `1px solid ${withAlpha(c.text, 0.24)}` },
          arrow: { color: c.button },
        },
      },
    },
  });
}
